"use client";

import { useRef, useCallback, KeyboardEvent } from "react";
import { SupportedLanguage } from "../types";

const LANGUAGES: { value: SupportedLanguage; label: string; icon: string }[] = [
  { value: "javascript", label: "JavaScript", icon: "JS" },
  { value: "php", label: "PHP", icon: "PHP" },
  { value: "python", label: "Python", icon: "PY" },
  { value: "java", label: "Java", icon: "JV" },
  { value: "sql", label: "SQL", icon: "SQL" },
  { value: "other", label: "Other", icon: "???" },
];

const LANGUAGE_COLORS: Record<SupportedLanguage, string> = {
  javascript: "text-yellow-400 bg-yellow-400/10 border-yellow-400/20",
  php: "text-indigo-400 bg-indigo-400/10 border-indigo-400/20",
  python: "text-blue-400 bg-blue-400/10 border-blue-400/20",
  java: "text-orange-400 bg-orange-400/10 border-orange-400/20",
  sql: "text-emerald-400 bg-emerald-400/10 border-emerald-400/20",
  other: "text-zinc-400 bg-zinc-400/10 border-zinc-400/20",
};

interface ErrorInputPanelProps {
  errorMessage: string;
  language: SupportedLanguage;
  likelyLanguage: SupportedLanguage | null;
  hasLanguageMismatch: boolean;
  isLoading: boolean;
  requestsUsed: number;
  rateLimit: number;
  onErrorMessageChange: (v: string) => void;
  onLanguageChange: (v: SupportedLanguage) => void;
  onUseLikelyLanguage: () => void;
  onExplain: () => void;
}

const PLACEHOLDER = `Paste your error message here…

Examples:
  TypeError: Cannot read properties of undefined (reading 'map')
  SyntaxError: Unexpected token '<'
  IndexError: list index out of range
  NullPointerException: Cannot invoke method on null`;

export default function ErrorInputPanel({
  errorMessage,
  language,
  likelyLanguage,
  hasLanguageMismatch,
  isLoading,
  requestsUsed,
  rateLimit,
  onErrorMessageChange,
  onLanguageChange,
  onUseLikelyLanguage,
  onExplain,
}: ErrorInputPanelProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isExhausted = requestsUsed >= rateLimit;
  const canSubmit = errorMessage.trim().length > 0 && !isLoading && !isExhausted;
  const charCount = errorMessage.length;

  const handleClear = useCallback(() => {
    onErrorMessageChange("");
    textareaRef.current?.focus();
  }, [onErrorMessageChange]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
        e.preventDefault();
        if (canSubmit) onExplain();
      }
    },
    [canSubmit, onExplain]
  );

  const selectedLang = LANGUAGES.find((l) => l.value === language)!;
  const likelyLangLabel = likelyLanguage ? LANGUAGES.find((l) => l.value === likelyLanguage)?.label : null;

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden shadow-xl">
      {/* Panel Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800 bg-zinc-900/80">
        <div className="flex items-center gap-2">
          <div className="flex gap-1.5">
            <span className="w-3 h-3 rounded-full bg-red-500/70" />
            <span className="w-3 h-3 rounded-full bg-amber-500/70" />
            <span className="w-3 h-3 rounded-full bg-emerald-500/70" />
          </div>
          <span className="text-zinc-500 text-xs ml-1" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
            error_input.txt
          </span>
        </div>

        <div className="flex items-center gap-2">
          {charCount > 0 && (
            <span className="text-zinc-600 text-xs" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
              {charCount} chars
            </span>
          )}
          {errorMessage && (
            <button
              onClick={handleClear}
              className="text-zinc-500 hover:text-zinc-300 text-xs px-2 py-1 rounded hover:bg-zinc-800 transition-all duration-150"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Textarea */}
      <div className="relative">
        {/* Line Numbers */}
        <div
          className="absolute left-0 top-0 bottom-0 w-10 flex flex-col pt-4 pb-4 text-right pr-2 select-none pointer-events-none"
          aria-hidden="true"
        >
          {Array.from({ length: Math.max(8, errorMessage.split("\n").length + 1) }, (_, i) => (
            <span
              key={i}
              className="text-zinc-700 leading-6"
              style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "11px" }}
            >
              {i + 1}
            </span>
          ))}
        </div>

        <textarea
          ref={textareaRef}
          value={errorMessage}
          onChange={(e) => onErrorMessageChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={PLACEHOLDER}
          rows={8}
          spellCheck={false}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          className="w-full bg-transparent text-zinc-200 placeholder-zinc-700 resize-none outline-none px-4 py-4 pl-12 leading-6 text-sm"
          style={{ fontFamily: "'IBM Plex Mono', monospace" }}
          aria-label="Error message input"
          disabled={isExhausted}
        />
      </div>

      {/* Bottom Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-4 py-3 border-t border-zinc-800 bg-zinc-900/50">
        {/* Language Selector */}
        <div className="flex flex-col items-start gap-2">
          {hasLanguageMismatch && likelyLanguage && likelyLangLabel && (
            <div className="flex flex-wrap items-center gap-2 text-xs bg-amber-500/10 border border-amber-500/25 text-amber-300 rounded-lg px-2.5 py-1.5">
              <span>
                This error looks like <strong>{likelyLangLabel}</strong>, but selected is <strong>{selectedLang.label}</strong>.
              </span>
              <button
                type="button"
                onClick={onUseLikelyLanguage}
                className="px-2 py-0.5 rounded border border-amber-400/35 hover:bg-amber-500/20 transition-colors"
                style={{ fontFamily: "'IBM Plex Mono', monospace" }}
              >
                Switch to {likelyLanguage.toUpperCase()}
              </button>
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap">
          <span className="text-zinc-500 text-xs font-medium uppercase tracking-wide">Language</span>
          <div className="flex flex-wrap gap-1.5">
            {LANGUAGES.map((lang) => (
              <button
                key={lang.value}
                onClick={() => onLanguageChange(lang.value)}
                className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-all duration-150 ${
                  language === lang.value
                    ? LANGUAGE_COLORS[lang.value]
                    : "text-zinc-500 bg-transparent border-zinc-700 hover:border-zinc-500 hover:text-zinc-300"
                }`}
                style={{ fontFamily: "'IBM Plex Mono', monospace" }}
              >
                {lang.icon}
              </button>
            ))}
          </div>
          <span className={`text-xs font-medium ${LANGUAGE_COLORS[language]} px-2 py-0.5 rounded border`}>
            {selectedLang.label}
          </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <span className="text-zinc-600 text-xs hidden sm:block" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
            ⌘ + Enter
          </span>

          {isExhausted ? (
            <div className="flex items-center gap-2 bg-red-950/50 border border-red-500/30 rounded-lg px-4 py-2">
              <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
              <span className="text-red-400 text-sm font-medium">Daily limit reached — try again tomorrow</span>
            </div>
          ) : (
            <button
              onClick={onExplain}
              disabled={!canSubmit}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-150 ${
                canSubmit
                  ? "bg-cyan-500 hover:bg-cyan-400 text-zinc-950 shadow-lg shadow-cyan-500/20 active:scale-95"
                  : "bg-zinc-800 text-zinc-600 cursor-not-allowed"
              }`}
              style={{ minWidth: "180px", justifyContent: "center" }}
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin w-4 h-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M21 12a9 9 0 11-6.219-8.56" />
                  </svg>
                  Analyzing…
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8" />
                    <path d="M21 21l-4.35-4.35M11 8v6M8 11h6" />
                  </svg>
                  Explain This Error
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}